'use client';

import Assessment from '@mui/icons-material/Assessment';
import Group from '@mui/icons-material/Group';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';

import {
  Box,
  Button as MuiButton,
  Fab,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
} from '@mui/material';
import {
  Flex,
  HeaderText,
  MEDIA_QUERY_MOBILE,
  Paragraph,
  columnStyles,
  rowStyles,
} from 'bouqei';
import styled, { css } from 'styled-components';
import { usePathname } from 'next/navigation';
import {
  Close,
  Instagram,
  Star,
  // Star,
  Start,
  Twitter,
  WorkspacePremium,
  YouTube,
} from '@mui/icons-material';
import { Fragment } from 'react';
import { rankingsOrder, teamNeedsOrder } from '#/lib/routing';
import colors from '#/lib/colors';
import { poppins, quest } from '#/utils/fonts';
import Image from 'next/image';
import { useShell } from '#/context/ShellContext';
import Link from 'next/link';
import { socialLinks } from '#/lib/social';

const rankingItems = rankingsOrder.map(position => ({
  text: position,
  href: `/draft/rankings/${position}`,
}));

const teamNeedsItems = teamNeedsOrder.map(position => ({
  text: position,
  href: `/draft/team-needs/${position}`,
}));

const navItems = [
  {
    text: 'Intro',
    href: '/draft',
    icon: <Start />,
  },
  {
    text: 'Team Needs',
    href: '/draft/team-needs',
    icon: <Group />,
    subNav: teamNeedsItems,
  },
  {
    text: 'Rankings',
    href: '/draft/rankings',
    icon: <WorkspacePremium />,
    subNav: rankingItems,
  },
  {
    text: 'Big Board',
    href: '/draft/big-board',
    icon: <FormatListNumbered />,
  },
  {
    text: 'Reports',
    href: '/draft/reports',
    icon: <Assessment />,
  },
  { text: 'Traits', href: '/traits', icon: <Star /> },
];

export default function DraftSidebar() {
  const pathname = usePathname();
  const { showSidebar: isOpen, setShowSidebar: toggleSidebar } = useShell();

  return (
    <>
      <SwipeableDrawer
        anchor="left"
        open={isOpen}
        onClose={() => toggleSidebar(false)}
        onOpen={() => toggleSidebar(true)}
      >
        <MobileSidebar className="mobile-sidebar">
          <Flex
            style={{ width: '100%', padding: '32px 0', textAlign: 'center' }}
            alignItems="center"
            justifyContent="center"
          >
            {/* <Logo className="brand" width="250px" /> */}
            <HeaderText
              format="light"
              style={{ textTransform: 'uppercase' }}
              type={2}
              className={poppins.className}
            >
              Badlands:
              <br />
              Draft Guide
            </HeaderText>
          </Flex>
          <Fab
            color="secondary"
            size="small"
            sx={{ position: 'absolute', top: '5%', right: '5%' }}
            aria-label="add"
            onClick={() => toggleSidebar(false)}
          >
            <Close />
          </Fab>
          <Box
            display="flex"
            flexDirection="column"
            flex="1"
            justifyContent="center"
            width="100%"
            pb="25%"
            gap="16px"
          >
            {navItems.map(item => (
              <Fragment key={`mobile-${item.text}`}>
                <ListItem disablePadding>
                  <ListItemButton
                    href={item.href}
                    // LinkComponent={Link}
                    className={pathname === item.href ? 'active' : ''}
                  >
                    <ListItemIcon sx={{ fill: colors.white }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        color: colors.white,
                        fontSize: '1.25rem',
                        fontWeight: '600',
                      }}
                      classes={{
                        primary: quest.className,
                      }}
                      primary={item.text}
                    />
                  </ListItemButton>
                </ListItem>
                {item.subNav && pathname.includes(item.href) && (
                  <MobileSubNavGrid>
                    {item.subNav.map(subItem => (
                      <MuiButton
                        key={`${item.text}-${subItem.href}`}
                        component="a"
                        color="secondary"
                        variant="contained"
                        href={subItem.href}
                      >
                        {subItem.text}
                      </MuiButton>
                    ))}
                  </MobileSubNavGrid>
                )}
              </Fragment>
            ))}
          </Box>
        </MobileSidebar>
      </SwipeableDrawer>
      <Container>
        <StaticSidebar className="sidebar-list">
          <Brand>
            <Image
              src="/images/badlands_outline.png"
              width="1680"
              height="721"
              alt="TOJ Badlands"
            />
          </Brand>
          <Title>
            <HeaderText
              type={5}
              className={poppins.className}
              weight={500}
              format="dark"
            >
              Draft Guide
            </HeaderText>
          </Title>
          {navItems.map(item => (
            <Fragment key={item.text}>
              <ListItem disablePadding key={item.text}>
                <ListItemButton
                  className={pathname === item.href ? 'active' : ''}
                  // LinkComponent={Link}
                  selected={item.href === pathname}
                  color="primary"
                  href={item.href}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    classes={{
                      primary: quest.className,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {item.subNav && pathname.includes(item.href) && (
                <SubNavGrid>
                  {item.subNav.map(subItem => (
                    <MuiButton
                      key={`${item.text}-${subItem.href}`}
                      component="a"
                      color="primary"
                      variant="contained"
                      href={subItem.href}
                    >
                      {subItem.text}
                    </MuiButton>
                  ))}
                </SubNavGrid>
              )}
            </Fragment>
          ))}
          <Social>
            <Paragraph format="dark" weight={400}>
              Follow us on social!
            </Paragraph>
            <SocialGrid>
              <Link href={socialLinks.twitter}>
                <Twitter sx={{ fill: colors.dark, fontSize: '2rem' }} />
              </Link>
              <Link href={socialLinks.instagram}>
                <Instagram sx={{ fill: colors.dark, fontSize: '2rem' }} />
              </Link>
              <Link href={socialLinks.youtube}>
                <YouTube sx={{ fill: colors.dark, fontSize: '2rem' }} />
              </Link>
            </SocialGrid>
          </Social>
        </StaticSidebar>
      </Container>
    </>
  );
}

const linkStyles = css`
  li a {
    font-size: 1.2rem;
  }

  li a span {
    font-size: inherit;
    font-weight: 500;
  }

  li a:hover span {
    color: ${colors.secondary};
  }

  li a.active span {
    color: ${colors.primary};
  }
`;

const MobileSidebar = styled(List)`
  width: 100vw;
  height: 100%;
  background-color: ${colors.primary};
  ${linkStyles};
  ${columnStyles('flex-start', 'flex-start')}

  li a {
    font-size: 1.25rem;
  }

  li a span {
    font-size: inherit;
  }

  li a.active span {
    color: ${colors.accent};
  }

  li svg {
    fill: ${colors.white};
  }
`;

const Container = styled.div`
  width: 22vw;
  height: 100vh;
  /* padding: 16px 0; */
  position: sticky;
  top: 0;
  background-color: ${colors.light};

  @media ${MEDIA_QUERY_MOBILE} {
    display: none;
  }
`;

const StaticSidebar = styled(List)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  ${linkStyles};
  /* border-radius: 16px; */
  /* box-shadow: rgba(0, 0, 0, 0.3) 0 1px 3px; */
`;

const SubNavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 8px 16px;
  gap: 8px;
`;

const MobileSubNavGrid = styled.div`
  ${rowStyles('flex-start', 'flex-start')};
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 16px;
`;

const Brand = styled.div`
  width: 100%;
  height: 65px;
  border-bottom: solid 1px ${colors.grey};
  position: relative;
  ${rowStyles('flex-start', 'flex-start')};

  img {
    height: 100%;
    width: auto;
  }
`;

const Title = styled.div`
  padding: 16px;
`;

const Social = styled(Box)`
  border-top: solid 1px ${colors.grey};
  padding: 16px 2.5%;
  text-align: center;
`;

const SocialGrid = styled.div`
  ${rowStyles('center', 'center')}
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 0;
`;
